import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'
import { tokens } from '@/theme'

interface Props {
  /** Called when the user clicks "Back". */
  onBack: () => void
  /** Crumbs rendered after "Back" — typically a chain of clickable parents and the current node. */
  children: ReactNode
}

/**
 * Slim crumb-band rendered above detail views. Replaces the full Hero on
 * sub-pages so the eye stays on the content.
 *
 * Mirrors the reference SPA's pattern: back button on the left, chevron-
 * separated crumbs to the right.
 */
export function Breadcrumb({ onBack, children }: Props) {
  return (
    <section
      style={{
        background: tokens.paper,
        borderBottom: `1px solid ${tokens.line}`,
      }}
    >
      <div
        className="orion-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `14px ${tokens.containerPadX}px`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          minHeight: 44,
          flexWrap: 'wrap',
        }}
      >
        <button
          onClick={onBack}
          aria-label="Back"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: tokens.ink3,
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            fontFamily: tokens.fontDisplay,
            transition: `color ${tokens.durFast} ${tokens.easeStandard}`,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = tokens.ink
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = tokens.ink3
          }}
        >
          <ArrowLeft size={11} strokeWidth={1.8} /> Back
        </button>
        <div style={{ width: 1, height: 14, background: tokens.line2 }} />
        <nav
          aria-label="Breadcrumb"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 11,
            color: tokens.ink3,
            fontFamily: tokens.fontDisplay,
            fontWeight: tokens.weightMedium,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            flexWrap: 'wrap',
          }}
        >
          {children}
        </nav>
      </div>
    </section>
  )
}
